import * as React from "react";
import { canonicalAnalyticsRoute } from "~/lib/analytics-events";
import { trackEvent } from "~/lib/posthog-client";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(_error: Error): void {
    trackEvent("page_error_shown", {
      error_type: "runtime",
      route: canonicalAnalyticsRoute(typeof window !== "undefined" ? window.location.pathname : "/unknown"),
    });
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "50vh",
          padding: "2rem",
          fontFamily: "Inter, system-ui, -apple-system, sans-serif",
          color: "var(--text, #1a1a1a)",
        }}
      >
        <div
          style={{
            maxWidth: "28rem",
            width: "100%",
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: "3rem",
              height: "3rem",
              margin: "0 auto 1.5rem",
              borderRadius: "13px",
              background: "var(--surface-raised, #f5f5f5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.25rem",
            }}
          >
            !
          </div>

          <h2
            style={{
              fontSize: "1.125rem",
              fontWeight: 600,
              margin: "0 0 0.5rem",
              letterSpacing: "-0.01em",
            }}
          >
            Something went wrong
          </h2>

          <p
            style={{
              fontSize: "0.875rem",
              color: "var(--text-muted, #6b7280)",
              margin: "0 0 1.5rem",
              lineHeight: 1.5,
            }}
          >
            An unexpected error occurred. Please try again or reload the page.
          </p>

          <button
            onClick={this.handleReset}
            style={{
              padding: "0.5rem 1.25rem",
              fontSize: "0.875rem",
              fontWeight: 500,
              color: "var(--text-inverse, #fff)",
              background: "var(--text, #1a1a1a)",
              border: "none",
              borderRadius: "10px",
              cursor: "pointer",
              transition: "opacity 300ms",
              fontFamily: "inherit",
            }}
            onMouseEnter={(e) => {
              (e.target as HTMLButtonElement).style.opacity = "0.85";
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLButtonElement).style.opacity = "1";
            }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}
